import {useEffect} from "react"
import {Chip, CircularProgress, Divider, Stack, Typography, useTheme} from "@mui/joy"
import {Check} from "lucide-react"
import {useNavigate} from "react-router-dom"
import {useTranslation} from "react-i18next"
import Card from "../components/Card"
import Button from "../components/Button"
import {useAuth} from "../hooks/useAuth"
import {useAsync} from "../hooks/useAsyncResult"
import {getPlans} from "../services/api/plans"
import {activePalette, applyTransparency} from "../theme"
import {PLANS, Plan, formatAmount, isSubscriptionActive} from "../values/plans"
import {LocalizedText, PlanOffer} from "../types/transaction"
import PageContainer from "../components/layout/PageContainer"

/**
 * The plans as the bundle ships them.
 *
 * Only ever a fallback: the catalogue is edited from the dashboard, so what is on sale comes from
 * the API. These keep the page from being blank when it cannot be reached.
 */
const bundledOffer = (plan: Plan): PlanOffer => ({
    tier: plan.tier,
    name: plan.name,
    price: plan.price,
    currency: plan.currency,
    billingCycle: plan.billingCycle,
    dailyPredictionsLimit: plan.dailyPredictions ?? 0,
    savingPercent: plan.savingPercent,
    recommended: plan.recommended
})

/** Copy the plan carries, in the language being read, or the wording the bundle ships with */
const localized = (value: LocalizedText | undefined, language: string, fallbackKey?: string,
                   t?: (key: string) => string) => {
    const written = value?.[language === "en" ? "en" : "fr"] || value?.fr || value?.en

    return written || (fallbackKey && t ? t(fallbackKey) : "")
}

/** Access period as the plan sheet writes it, for a plan the API did not spell one out for */
const DURATION_KEYS: { [tier: string]: string } = {
    standard: "standardDuration",
    premium: "premiumDuration",
    platinum: "platinumDuration"
}

const TAGLINE_KEYS: { [tier: string]: string } = {
    standard: "standardTagline",
    premium: "premiumTagline",
    platinum: "platinumTagline"
}

/** Feature lines the bundle ships per tier, used when the catalogue carries none */
const FEATURE_KEYS: { [tier: string]: string[] } = PLANS.reduce((keys, plan) => ({
    ...keys,
    [plan.tier]: plan.features
}), {})

const PlanCard = (props: { plan: PlanOffer, current: boolean, onChoose: (plan: PlanOffer) => void }) => {
    const {t, i18n} = useTranslation()
    const palette = activePalette(useTheme())
    const {plan, current} = props

    const features = plan.features?.length
        ? plan.features.map(feature => localized(feature, i18n.language))
        : (FEATURE_KEYS[plan.tier] || []).map(key => t(key))

    return <Card sx={{
        flex: 1,
        gap: 2,
        position: "relative",
        overflow: "visible",
        borderColor: plan.recommended ? palette.primary["600"] : "rgba(255,255,255,0.1)",
        borderWidth: plan.recommended ? 2 : 1,
        background: plan.recommended
            ? applyTransparency(palette.primary["500"], 0.12)
            : "rgba(255,255,255,0.05)"
    }}>
        {plan.recommended && <Chip size={"sm"} variant={"solid"} color={"primary"}
                                   sx={{position: "absolute", top: -13, left: "50%", transform: "translateX(-50%)"}}>
            {t("recommendedBadge")}
        </Chip>}

        <Stack gap={0.5}>
            <Typography level={"body-xs"} textColor={"primary.700"} letterSpacing={"0.1em"}>
                {localized(plan.durationLabel, i18n.language, DURATION_KEYS[plan.tier], t)}
            </Typography>
            <Typography level={"h3"}>{plan.name}</Typography>
            <Typography level={"body-sm"}>
                {localized(plan.tagline, i18n.language, TAGLINE_KEYS[plan.tier], t)}
            </Typography>
        </Stack>

        <Stack gap={1}>
            <Stack direction={"row"} alignItems={"baseline"} gap={1} flexWrap={"wrap"}>
                <Typography level={"h2"}>{formatAmount(plan.price)}</Typography>
                <Typography level={"body-sm"}>{t("currencyLabel")}</Typography>
            </Stack>

            {/* Only the plans that beat the shortest one carry a saving, so only they show one */}
            {!!plan.savingPercent && <Chip size={"sm"} variant={"soft"} color={"success"}
                                           sx={{alignSelf: "flex-start"}}>
                {t("saveVsStandard", {percent: plan.savingPercent})}
            </Chip>}
        </Stack>

        <Divider/>

        <Stack gap={1}>
            {features.map((feature, index) =>
                <Typography key={`${plan.tier}-${index}`} level={"body-sm"}
                            startDecorator={<Check size={16} style={{color: palette.primary["700"]}}/>}>
                    {feature}
                </Typography>)}
        </Stack>

        <Button disabled={current}
                variant={plan.recommended ? "solid" : "soft"}
                onClick={() => props.onChoose(plan)}
                sx={{marginTop: "auto"}}>
            {current ? t("currentPlan") : t("subscribe")}
        </Button>
    </Card>
}

/**
 * The plans on sale.
 *
 * Read from the API, which is where they are edited from: choosing one goes straight to the
 * checkout, with the plan carried in the url so the page can be shared or come back to.
 */
const Pricing = () => {
    const {t} = useTranslation()
    const auth = useAuth()
    const navigate = useNavigate()
    const catalogue = useAsync(getPlans, false, false, true)

    useEffect(() => {
        catalogue.execute()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const subscription = auth.user?.subscription
    const currentTier = isSubscriptionActive(subscription) ? subscription.tier : undefined
    // The bundle only stands in for a catalogue that could not be read
    const plans = catalogue.data?.response?.length ? catalogue.data.response : PLANS.map(bundledOffer)

    const choose = (plan: PlanOffer) => {
        if (!auth.user)
            return auth.requestSignIn?.("guardReasonAnalyze")

        navigate(`/checkout?plan=${plan.tier}`)
    }

    return <PageContainer gap={{xs: 3, md: 4}} width={{md: "48rem", lg: "68rem"}}>
        <Stack alignItems={"center"} gap={1}>
            <Typography level={"body-xs"} textColor={"primary.700"} letterSpacing={"0.2em"}>
                {t("pricingEyebrow")}
            </Typography>
            <Typography level={"h1"} textAlign={"center"} fontSize={{xs: "1.9rem", sm: undefined}}>
                {t("pricingTitle")}
            </Typography>
            <Typography level={"body-sm"} textAlign={"center"}>
                {t("pricingSubtitle")}
            </Typography>
        </Stack>

        {catalogue.loading
            ? <Stack alignItems={"center"} paddingY={4}>
                <CircularProgress/>
            </Stack>
            : <Stack direction={{xs: "column", md: "row"}} gap={3} alignItems={"stretch"}>
                {plans.map(plan => <PlanCard key={plan.tier}
                                             plan={plan}
                                             current={currentTier === plan.tier}
                                             onChoose={choose}/>)}
            </Stack>}
    </PageContainer>
}

export default Pricing
